import { useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { X, FileText } from 'lucide-react';
import { cn } from './cn';

/**
 * Ant Design `Upload` replacement covering the app's usage: a click-to-pick
 * trigger (children) whose selected file(s) are handed to `beforeUpload`
 * (which does the actual upload and returns `Upload.LIST_IGNORE`/`false`,
 * so this component never manages an internal file list), plus `Upload.Dragger`.
 */
const LIST_IGNORE = '__LIST_IGNORE__' as const;

export interface UploadFile {
  uid: string;
  name: string;
  status?: string;
  url?: string;
}

export interface UploadProps {
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  showUploadList?: boolean;
  beforeUpload?: (file: File, fileList: File[]) => boolean | string | void | Promise<any>;
  onRemove?: (file: any) => void;
  fileList?: UploadFile[];
  maxCount?: number;
  listType?: string;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

async function run(files: File[], maxCount: number | undefined, beforeUpload?: UploadProps['beforeUpload']) {
  const arr = maxCount ? files.slice(0, maxCount) : files;
  for (const f of arr) {
    // Consumers upload inside beforeUpload and return LIST_IGNORE/false; the
    // return value is intentionally ignored — we never manage a list here.
    if (beforeUpload) await beforeUpload(f, arr);
  }
}

function UploadBase({ accept, multiple, disabled, beforeUpload, maxCount, children, className, style }: UploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <span className={cn('inline-block', className)} style={style}>
      <span
        onClick={() => { if (!disabled) inputRef.current?.click(); }}
        className={cn(disabled && 'pointer-events-none opacity-60')}
      >
        {children}
      </span>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        hidden
        onChange={(e) => { run(Array.from(e.target.files ?? []), maxCount, beforeUpload); e.target.value = ''; }}
      />
    </span>
  );
}

function Dragger({
  accept, multiple, disabled, beforeUpload, onRemove, fileList = [], maxCount, children, className, style,
}: UploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);
  return (
    <div className={className} style={style}>
      <div
        onClick={() => { if (!disabled) inputRef.current?.click(); }}
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); if (!disabled) run(Array.from(e.dataTransfer.files), maxCount, beforeUpload); }}
        className={cn(
          'flex cursor-pointer flex-col items-center gap-1.5 rounded-card border-2 border-dashed px-4 py-8 text-center transition-colors',
          drag ? 'border-brand-400 bg-brand-50/50 dark:bg-brand-900/20' : 'border-line hover:border-brand-300',
          disabled && 'pointer-events-none opacity-60',
        )}
      >
        {children}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        hidden
        onChange={(e) => { run(Array.from(e.target.files ?? []), maxCount, beforeUpload); e.target.value = ''; }}
      />
      {fileList.length > 0 && (
        <ul className="mt-2 space-y-1">
          {fileList.map((f) => (
            <li key={f.uid} className="flex items-center gap-2 rounded-control border border-line bg-surface px-2.5 py-1.5 text-sm text-content">
              <FileText className="size-4 shrink-0 text-subtle" />
              <span className="min-w-0 flex-1 truncate">{f.name}</span>
              <button type="button" aria-label="Remove" onClick={() => onRemove?.(f)} className="shrink-0 text-subtle hover:text-danger-500">
                <X className="size-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export const Upload = Object.assign(UploadBase, { Dragger, LIST_IGNORE });
